'use client'

import Script from 'next/script'
import { useCallback } from 'react'

import { asyncGlobalVariable } from '../../../../lib/utils'
import { useAuthentication } from '../../../_providers/AuthenticationProvider.client'
import { useConsentPolicy } from '../../../_providers/ConsentPolicyProvider.client'
import { useSession } from '../../../_providers/SessionProvider.client'

const heap$ = asyncGlobalVariable(() => (globalThis.heap?.loaded ? globalThis.heap : undefined))
const heapReadyCb$ = asyncGlobalVariable(() => globalThis.heapReadyCb)

const isHeapPresent = () => {
  return typeof globalThis.heap !== 'undefined' && globalThis.heap.loaded === true
}

export const useHeap = () => {
  const sendHeapEvent = useCallback(
    ({ eventName, props }: { eventName: string; props: Record<string, string> }) => {
      if (typeof window === 'undefined') return
      if (isHeapPresent()) {
        globalThis.heap.track(eventName, props)
      } else {
        heap$.subscribe(() => {
          if (isHeapPresent()) {
            globalThis.heap.track(eventName, props)
          }
        })
      }
    },
    []
  )

  const addHeapEventProperties = useCallback(({ props }: { props: Record<string, string> }) => {
    if (typeof window === 'undefined') return
    if (isHeapPresent()) {
      globalThis.heap.addEventProperties(props)
    } else {
      heap$.subscribe(() => {
        if (isHeapPresent()) {
          globalThis.heap.addEventProperties(props)
        }
      })
    }
  }, [])

  return {
    sendHeapEvent,
    addHeapEventProperties,
  }
}

export const HeapScript = () => {
  const {
    consentPolicy: { performance },
  } = useConsentPolicy()
  const { attributes } = useSession()
  const { isLoggedIn } = useAuthentication()

  const heapId = process.env.NEXT_PUBLIC_HEAP_APP_ID

  if (!heapId || performance !== 'allowed') return null

  const buildFigsProperties = function () {
    const userAgent = typeof window === 'undefined' ? 'SSR' : window.navigator.userAgent
    const lowerCaseUserAgent = userAgent.toLowerCase()
    let appBrowser = ''

    if (
      lowerCaseUserAgent.includes('musical_ly') ||
      lowerCaseUserAgent.includes('bytedancewebview') ||
      lowerCaseUserAgent.includes('tiktok') ||
      lowerCaseUserAgent.includes('sparknav')
    ) {
      appBrowser = 'tiktok'
    } else if (lowerCaseUserAgent.includes('instagram')) {
      appBrowser = 'instagram'
    } else if (
      lowerCaseUserAgent.includes('fb_iab') ||
      lowerCaseUserAgent.includes('fban') ||
      lowerCaseUserAgent.includes('fbav') ||
      lowerCaseUserAgent.includes('fb4a')
    ) {
      appBrowser = 'facebook'
    } else if (lowerCaseUserAgent.includes('messenger')) {
      appBrowser = 'messenger'
    } else if (lowerCaseUserAgent.includes('snapchat')) {
      appBrowser = 'snapchat'
    } else if (lowerCaseUserAgent.includes('pinterest')) {
      appBrowser = 'pinterest'
    } else if (lowerCaseUserAgent.includes('twitter')) {
      appBrowser = 'twitter'
    } else if (lowerCaseUserAgent.includes('reddit')) {
      appBrowser = 'reddit'
    }

    return Promise.resolve({
      '@figs:visitor-id': attributes.visitorId,
      '@figs:session-id': attributes.sessionId,
      '@figs:user-agent': window.navigator.userAgent,
      '@figs:in-app-source': appBrowser,
      'Logged In': isLoggedIn,
    })
  }

  const adapterName = 'figsPropertiesAdapter'
  const setFigsProperties = () => {
    return buildFigsProperties().then(props => {
      heap.clearEventProperties()
      heap.addEventProperties(props)
      heap.markAdapterAsResolved(adapterName)
    })
  }

  const adapter = { name: adapterName, fn: setFigsProperties }
  const config = { isBlocking: true, blockDurationInMs: 5000 }

  heapReadyCb$.subscribe(() => {
    if (typeof heap.load !== 'undefined') {
      heap.load(heapId, { compressCookies: true })
      heap.addAdapter(adapter, config)
    }
  })

  return (
    <Script
      id='heap-analytics'
      strategy='afterInteractive' // Analytics is set to afterInteractive
      dangerouslySetInnerHTML={{
        __html: `
        window.heapReadyCb=window.heapReadyCb||[],window.heap=window.heap||[],heap.load=function(e,t){window.heap.envId=e,window.heap.clientConfig=t=t||{},window.heap.clientConfig.shouldFetchServerConfig=!1;var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="https://cdn.us.heap-api.com/config/"+e+"/heap_config.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(a,r);var n=["init","startTracking","stopTracking","track","resetIdentity","identify","identifyHashed","getSessionId","getUserId","getIdentity","addUserProperties","addEventProperties","removeEventProperty","clearEventProperties","addAccountProperties","addAdapter","addTransformer","addTransformerFn","onReady","addPageviewProperties","removePageviewProperty","clearPageviewProperties","trackPageview"],i=function(e){return function(){var t=Array.prototype.slice.call(arguments,0);window.heapReadyCb.push({name:e,fn:function(){heap[e]&&heap[e].apply(heap,t)}})}};for(var p=0;p<n.length;p++)heap[n[p]]=i(n[p])};
      `,
      }}
    />
  )
}
